import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/** A pipeline step's colour: done, needs attention, or neutral. */
export type SnapshotPipelineStepTone = "done" | "attention" | "neutral";

interface SnapshotPipelineStepProps {
  marker: ReactNode;
  tone?: SnapshotPipelineStepTone;
  label: string;
  value?: ReactNode;
  sub: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}

/**
 * One step of a Snapshot page's pipeline strip: its marker (a number, or ✓ when done), label,
 * value, a line under it and any extra content (e.g. a progress bar); an action on the right.
 */
export const SnapshotPipelineStep = (props: SnapshotPipelineStepProps) => {
  const { marker, tone = "neutral", label, value, sub, action, children } = props;

  let markerBgcolor = "action.hover";
  let markerColor = "text.secondary";
  if (tone === "done") {
    markerBgcolor = "surface.success";
    markerColor = "success.main";
  }
  else if (tone === "attention") {
    markerBgcolor = "surface.warning";
    markerColor = "warning.main";
  }

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: "flex-start",
        minWidth: 0,
        px: 2,
        py: 1.5,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          bgcolor: markerBgcolor,
          borderRadius: "50%",
          color: markerColor,
          display: "flex",
          flexShrink: 0,
          fontSize: 13,
          fontWeight: 700,
          height: 28,
          justifyContent: "center",
          width: 28,
        }}
      >
        {marker}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          color="text.disabled"
          sx={{
            display: "block",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
          variant="caption"
        >
          {label}
        </Typography>
        {value && (
          <Typography
            sx={{ fontWeight: 600 }}
            variant="body1"
          >
            {value}
          </Typography>
        )}
        <Typography
          color="text.secondary"
          component="div"
          variant="body2"
        >
          {sub}
        </Typography>
        {children}
      </Box>
      {action && (
        <Box
          sx={{
            alignSelf: "center",
            flexShrink: 0,
          }}
        >
          {action}
        </Box>
      )}
    </Stack>
  );
};
